import { useState, useRef } from 'react';
import { apiClient } from '@/lib/api';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';
import { motion, AnimatePresence } from 'framer-motion';

interface AnalysisResult {
  patterns?: any[];
  indicators?: any[];
  features?: any;
  chart_type?: string;
  confidence?: number;
}

export default function ImageAnalysisPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      // Perform comprehensive analysis
      const [analysisResponse, patternsResponse, indicatorsResponse, featuresResponse] = await Promise.all([
        apiClient.uploadImageForAnalysis(selectedFile),
        apiClient.detectPatterns(selectedFile),
        apiClient.detectTechnicalIndicators(selectedFile),
        apiClient.extractFeatures(selectedFile)
      ]);

      if (analysisResponse.error) {
        throw new Error(analysisResponse.error);
      }

      // Combine all results
      const combinedResult: AnalysisResult = {
        patterns: (patternsResponse.data as any)?.patterns || [],
        indicators: (indicatorsResponse.data as any)?.indicators || [],
        features: (featuresResponse.data as any)?.features || {},
        chart_type: (analysisResponse.data as any)?.chart_type,
        confidence: (analysisResponse.data as any)?.confidence
      };

      setAnalysisResult(combinedResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setError(null);
    }
  };

  const clearAnalysis = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setAnalysisResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30">
      {/* Header */}
      <div className="border-b border-border-primary bg-bg-secondary/50 backdrop-blur-xl sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl tracking-tight font-semibold text-text-primary">
              Neural Chart Recognition
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div className="space-y-6">
            <GlassCard variant="elevated" className="p-6">
              <h2 className="text-lg font-medium text-text-primary mb-4">
                Chart Ingestion
              </h2>
              
              {/* File Upload Area */}
              <div
                className="border-2 border-dashed border-border-primary rounded-2xl p-8 text-center hover:border-brand-teal/50 transition-all cursor-pointer bg-bg-secondary/30 relative overflow-hidden group"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {previewUrl ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-4"
                  >
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="max-w-full h-64 object-contain mx-auto rounded-xl shadow-premium border border-border-primary"
                    />
                    <p className="text-brand-teal-light text-sm font-medium">
                      {selectedFile?.name} locked
                    </p>
                  </motion.div>
                ) : (
                  <div className="space-y-4 py-8">
                    <div className="mx-auto w-16 h-16 bg-brand-teal/10 rounded-full flex items-center justify-center border border-brand-teal/20 group-hover:scale-110 transition-transform">
                      <svg className="w-8 h-8 text-brand-teal-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-text-primary text-lg font-medium">
                        Drop chart for neural scan
                      </p>
                      <p className="text-text-secondary text-sm mt-2 font-light">
                        Supports PNG, JPG, WEBP formats
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="absolute inset-0 bg-brand-teal/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <GlassButton
                  onClick={handleAnalyze}
                  disabled={!selectedFile || isAnalyzing}
                  variant="primary"
                  glowEffect
                  className="flex-1"
                >
                  {isAnalyzing ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                      Scanning...
                    </div>
                  ) : (
                    'Run Neural Analysis'
                  )}
                </GlassButton>
                
                <GlassButton
                  onClick={clearAnalysis}
                  variant="ghost"
                  className="text-text-secondary"
                >
                  Purge
                </GlassButton>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-brand-plum/10 border border-brand-plum/20 rounded-xl">
                  <p className="text-brand-plum-light text-xs font-medium text-center">{error}</p>
                </div>
              )}
            </GlassCard>
            
            {/* Instructions */}
            <GlassCard className="p-6">
              <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-4">
                Scan Protocol
              </h3>
              <div className="grid grid-cols-1 gap-4 text-sm">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 border border-brand-teal/20 flex items-center justify-center text-[10px] font-bold text-brand-teal-light">01</div>
                  <p className="text-text-secondary"><span className="text-text-primary font-medium">Upload Chart:</span> Supply a high-resolution screenshot of any trading instrument.</p>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 border border-brand-teal/20 flex items-center justify-center text-[10px] font-bold text-brand-teal-light">02</div>
                  <p className="text-text-secondary"><span className="text-text-primary font-medium">Neural processing:</span> Our deep-learning models detect geometry, volume profiles, and candlestick clusters.</p>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 border border-brand-teal/20 flex items-center justify-center text-[10px] font-bold text-brand-teal-light">03</div>
                  <p className="text-text-secondary"><span className="text-text-primary font-medium">Alpha Extraction:</span> Identify support/resistance, oscillators, and trend reversals instantly.</p>
                </div>
              </div>
            </GlassCard>
          </div>

          {/* Analysis Results */}
          <div className="space-y-6">
            <AnimatePresence mode="wait">
              {analysisResult ? (
                <motion.div 
                  key="results"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Chart Type Detection */}
                  <GlassCard variant="glow" className="p-6">
                    <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
                       <div className="w-2 h-2 rounded-full bg-brand-teal-light animate-pulse shadow-[0_0_8px_rgba(45,212,191,0.8)]" />
                      Classification
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-bg-secondary/50 rounded-xl border border-border-primary">
                        <span className="text-text-secondary text-sm">Detected Category</span>
                        <span className="text-brand-teal-light font-bold">
                          {analysisResult.chart_type || 'Unknown Asset'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-bg-secondary/50 rounded-xl border border-border-primary">
                        <span className="text-text-secondary text-sm">Model Confidence</span>
                        <span className="text-text-primary font-mono font-bold">
                          {analysisResult.confidence ? `${(analysisResult.confidence * 100).toFixed(1)}%` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </GlassCard>

                  {/* Pattern Detection */}
                  {analysisResult.patterns && analysisResult.patterns.length > 0 && (
                    <GlassCard variant="elevated" className="p-6 border-l-4 border-l-brand-teal">
                      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-4">
                        Geometry Patterns
                      </h3>
                      <div className="space-y-2">
                        {analysisResult.patterns.map((pattern: any, index: number) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-bg-secondary/50 rounded-xl border border-border-primary hover:border-brand-teal/30 transition-colors">
                            <span className="text-text-primary font-medium">{pattern.name}</span>
                            <span className="tabular-nums font-bold text-brand-teal-light">
                              {pattern.confidence ? `${(pattern.confidence * 100).toFixed(1)}%` : 'CONSENSUS'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </GlassCard>
                  )}

                  {/* Technical Indicators */}
                  {analysisResult.indicators && analysisResult.indicators.length > 0 && (
                    <GlassCard variant="elevated" className="p-6 border-l-4 border-l-brand-plum">
                      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-4">
                        Oscillators & Indicators
                      </h3>
                      <div className="space-y-2">
                        {analysisResult.indicators.map((indicator: any, index: number) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-bg-secondary/50 rounded-xl border border-border-primary group">
                            <span className="text-text-primary font-medium">{indicator.name}</span>
                            <span className="text-text-secondary text-xs font-mono group-hover:text-brand-plum-light transition-colors">
                              {indicator.value || 'DETECTED'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </GlassCard>
                  )}

                  {/* Feature Analysis */}
                  {analysisResult.features && Object.keys(analysisResult.features).length > 0 && (
                    <GlassCard className="p-6">
                      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-4">
                        Neural Feature Matrix
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(analysisResult.features).map(([key, value]) => (
                          <div key={key} className="p-3 bg-bg-secondary/30 rounded-xl border border-border-primary">
                            <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-1">
                              {key.replace(/_/g, ' ')}
                            </div>
                            <div className="text-text-primary font-medium truncate">
                              {typeof value === 'number' ? value.toFixed(4) : String(value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </GlassCard>
                  )}
                </motion.div>
              ) : (
                <motion.div 
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center py-20 text-center"
                >
                  <div className="w-20 h-20 rounded-full bg-bg-secondary border border-border-primary flex items-center justify-center mb-6 text-text-tertiary animate-pulse">
                     <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                     </svg>
                  </div>
                  <h3 className="text-xl font-medium text-text-primary mb-2">Awaiting Data</h3>
                  <p className="text-text-secondary max-w-xs mx-auto font-light leading-relaxed">
                    Initialize a neural scan by supplying a chart image in the ingestion portal.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
} 



